import { db } from '../firebaseApp'
import i18n from '@/services/Translations'

export enum GuestState {
  Welcome = 'welcome',
  Service = 'service',
  Idle = 'idle',
  Returning = 'returning',
  WatchingVideo = 'watchingVideo',
}

export type Timer = {
  userId: string
  startedOn: number
  duration: number
  endsOn: number
}

export type Guest = {
  id: string
  isActive: boolean
  name: string
  agora: boolean
  state: GuestState
  canSeeItself: boolean
  calledWaiter: boolean
  room?: string
  isMuted: boolean
  waiterId?: string
  waiterName?: string
  isWaiter?: boolean
  sessionEnded: boolean
  sessionStartedOn: number
  message?: string
  timerFinished?: boolean
  refreshUser?: boolean
  timer?: Timer
  watchingVideoId?: string
}

export type GuestList = {
  [index: string]: Guest
}

class Guests {
  async createWaiterGuest(restaurantId: string, seatingId: string, roomId: string, waiterId: string, waiterName: string | null = i18n.t('global.waiter') + '') {
    await this.createGuest(restaurantId, seatingId, waiterId, roomId, false)
    return db.ref(`restaurants/${restaurantId}/seatings/${seatingId}/guests/${waiterId}`).update({
      isWaiter: true,
      name: waiterName,
      state: GuestState.Idle,
    })
  }

  async removeWaiterGuest(restaurantId: string, seatingId: string, waiterId: string) {
    return db.ref(`restaurants/${restaurantId}/seatings/${seatingId}/guests/${waiterId}`).remove()
  }

  async createGuest(restaurantId: string, seatingId: string, userId: string, roomId: string | null = null, returningGuest: boolean = false) {
    const guestPath = `restaurants/${restaurantId}/seatings/${seatingId}/guests/${userId}`
    const existingGuest = (await db.ref(guestPath).once('value')).val()

    const guest: Partial<Guest> = {
      id: userId,
      name: '',
      isActive: false,
      state: GuestState.Welcome,
      canSeeItself: false, // User can't see itself is default
      calledWaiter: false,
      agora: false,
      isMuted: false,
      sessionStartedOn: new Date().getTime(),
      sessionEnded: false,
    }

    if (roomId) {
      // Check if room has enough space
      const guests:GuestList = await this.getActiveGuests(restaurantId, seatingId);
      const peopleInRoom = Object.keys(guests).filter(guestId => guests[guestId].room === roomId).length;
      // the max roomsize is 12, but this enables the waiter to join the party
      if (peopleInRoom < 13) {
        // there is enough space, go right through
        guest.isActive = true;
        guest.agora = true;
        guest.state = GuestState.Idle;
        guest.room = roomId;
      }
    }

    if (returningGuest) {
      guest.state = GuestState.Returning;
    }

    if (existingGuest) {
      guest.name = existingGuest.name ? existingGuest.name : ''
      guest.sessionStartedOn = existingGuest.sessionStartedOn ? existingGuest.sessionStartedOn : guest.sessionStartedOn
    }

    await db.ref(guestPath).set(guest)

    const userActiveRef = db.ref(guestPath).child('isActive')
    const connectedRef = db.ref('/.info/connected')
    connectedRef.on('value', s => {
      if (s.val()) {
        userActiveRef.onDisconnect().set(false)
        userActiveRef.set(true)
      }
    })

    return guest
  }

  async updateName(
    restaurantId: string,
    seatingId: string,
    userId: string,
    name: string,
  ) {
    return db.ref(`restaurants/${restaurantId}/seatings/${seatingId}/guests/${userId}`).update({
      name,
    })
  }

  async updateMessageToGuest(
    restaurantId: string,
    seatingId: string,
    userId: string,
    message: string,
  ) {
    return db.ref(`restaurants/${restaurantId}/seatings/${seatingId}/guests/${userId}`).update({
      message,
    })
  }

  filterActiveGuests(guests: GuestList) {
    const active = Object.keys(guests)
      .map(id => guests[id])
      .filter(({ isActive }) => isActive)
      .reduce((obj, guest) => ({ ...obj, [guest.id]: guest }), {})

    return active
  }

  observeActiveGuests(restaurantId: string, seatingId: string, f: (guests: GuestList | null) => void) {
    db.ref(`restaurants/${restaurantId}/seatings/${seatingId}/guests`).on('value', s => {
      const guests = s.val()
      if (!guests) {
        f(null)
      } else {
        const activeGuests = this.filterActiveGuests(guests)
        f(activeGuests)
      }
    })
  }

  async getActiveGuests(restaurantId: string, seatingId: string) {
    const snapshot = await db.ref(`restaurants/${restaurantId}/seatings/${seatingId}/guests`).once('value')
    const guests = snapshot.val();

    return guests ? this.filterActiveGuests(guests) : {};
  }

  // handled by waiter
  async moveGuestToRoom(
    restaurantId: string,
    seatingId: string,
    userId: string,
    room: string | null,
    waiterId: string,
    waiterName: string
  ) {
    if (!restaurantId || !seatingId || !userId) {
      return
    }

    await db.ref(`restaurants/${restaurantId}/seatings/${seatingId}/guests/${userId}`).update({
      room,
      waiterId,
      waiterName
    })
  }

  async setGuestOnAgora(restaurantId: string, seatingId: string, userId: string, onAgora: boolean) {
    await db.ref(`restaurants/${restaurantId}/seatings/${seatingId}/guests/${userId}`).update({
      agora: onAgora,
    })
  }

  async setGuestState(
    restaurantId: string,
    seatingId: string,
    userId: string,
    state: string,
    canSeeItself: boolean,
    waiterId: string | null,
    waiterName: string | null,
  ) {
    await db.ref(`restaurants/${restaurantId}/seatings/${seatingId}/guests/${userId}`).update({
      state,
      canSeeItself,
      calledWaiter: false,
      waiterId,
      waiterName,
      // With every state change, clear the previous messages
      message: '',
    })
  }

  async setGuestCanSeeItself(
    restaurantId: string,
    seatingId: string,
    userId: string,
    canSeeItself: boolean,
  ) {
    await db.ref(`restaurants/${restaurantId}/seatings/${seatingId}/guests/${userId}`).update({
      canSeeItself,
    })
  }

  async setEndGuestSession(
    restaurantId: string,
    seatingId: string,
    userId: string,
    waiterId: string,
    waiterName: string,
    isSessionEnded: boolean,
  ) {
    return db.ref(`restaurants/${restaurantId}/seatings/${seatingId}/guests/${userId}`).update({
      sessionEnded: isSessionEnded,
      waiterId,
      waiterName
    })
  }

  async setGuestRefreshSession(
    restaurantId: string,
    seatingId: string,
    userId: string,
    refreshUser: boolean,
  ) {
    return db.ref(`restaurants/${restaurantId}/seatings/${seatingId}/guests/${userId}`).update({
      refreshUser,
    })
  }

  async callWaiter(restaurantId: string, seatingId: string, userId: string, callWaiter: boolean) {
    await db.ref(`restaurants/${restaurantId}/seatings/${seatingId}/guests/${userId}`).update({
      calledWaiter: callWaiter,
    })
  }

  async addTimer(
    restaurantId: string,
    seatingId: string,
    userId: string,
    timer: Timer | null,
  ) {
    await db.ref(`restaurants/${restaurantId}/seatings/${seatingId}/guests/${userId}`).update({
      timer,
      timerFinished: false,
    })
  }

  async finishTimer(
    restaurantId: string,
    seatingId: string,
    userId: string,
  ) {
    await db.ref(`restaurants/${restaurantId}/seatings/${seatingId}/guests/${userId}`).transaction((guest) => {
      if (!guest.timerFinished) {
        guest.timerFinished = true;
      }
      return guest;
    });
  }

  async setWatchingVideoId(
    restaurantId: string,
    seatingId: string,
    userId: string,
    watchingVideoId: string | null,
  ) {
    await db.ref(`restaurants/${restaurantId}/seatings/${seatingId}/guests/${userId}`).update({
      watchingVideoId,
      state: !!watchingVideoId ? GuestState.WatchingVideo : GuestState.Idle,
    })
  }
}

export default new Guests()
